import React, { useId } from 'react';
import { SPEAKER_ROLE, resolveSpeaker } from './speakerRoles';

/**
 * 百分率を画面に出す文字にする (SCR-D-001 DP-D-002)。
 *
 * **API の値を小数 1 桁のまま出し、画面で丸めない** (1.6 で決定)。整数に丸めると、区間が 3 つあるとき
 * 合計が 100 にならないことがある (例 33.5／33.5／33.0 → 34／34／33)。API は合計を 100.0 にしている
 * (PBI-004 §2.3) ため、そのまま出せば合計は 100.0 になる。
 * 仮定: 整数の値（52 等）も「52.0」と小数 1 桁で出す（桁をそろえるため。SCR-D-001 に定めが無い。未確認）
 *
 * @param {number} value 0〜100
 * @returns {string}
 */
export function formatPercent(value) {
    return value.toFixed(1);
}

/** 発話量の比率の見出し (SCR-D-001 DP-D-002。1.5 で「発話量の比率（文字数）」と定まった) */
export const SPEECH_RATIO_HEADING = '発話量の比率（文字数）';

/**
 * 発話量の比率バー (SCR-D-001 DP-D-002、インタラクションスタイル §6)。
 *
 * 比率は「文字数」の比率 (#1247 前提 #1)。**画面の文言に「時間」を書かない。**
 * 左からエージェント・求職者、`unknown` が 0 より大きいときだけ 3 つ目に「不明」の区間を足す。
 * バーは装飾として読み上げず、値は文字で伝える。
 *
 * @param {{ratio: {agent: number, candidate: number, unknown: number}}} props 百分率 (0〜100、小数 1 桁)
 */
function SpeechRatioBar({ ratio }) {
    const headingId = useId();
    const roles = [SPEAKER_ROLE.agent, SPEAKER_ROLE.candidate];
    if (ratio.unknown > 0) roles.push(SPEAKER_ROLE.unknown);
    const segments = roles.map((role) => ({
        ...resolveSpeaker(role),
        percent: ratio[role],
    }));

    return (
        <section className="diar-ratio" aria-labelledby={headingId}>
            <h4 id={headingId} className="diar-section-heading">{SPEECH_RATIO_HEADING}</h4>
            <div className="diar-ratio-bar" aria-hidden="true">
                {segments.map((segment) => (
                    <span
                        key={segment.role}
                        className={`diar-ratio-segment diar-ratio-segment--${segment.role}`}
                        style={{ width: `${segment.percent}%` }}
                        data-testid={`diar-ratio-segment-${segment.role}`}
                    />
                ))}
            </div>
            <dl className="diar-ratio-values">
                {segments.map((segment) => (
                    <div key={segment.role} className={`diar-ratio-value diar-ratio-value--${segment.role}`}>
                        <dt>
                            {/* 名前の文字色は役割で変えないため、バーの区間との対応を ■ の色で示す (DP-D-002。凡例と同じ形) */}
                            <span className={`diar-ratio-mark diar-ratio-mark--${segment.role}`} aria-hidden="true">■</span>
                            {segment.label}
                        </dt>
                        <dd>{formatPercent(segment.percent)}%</dd>
                    </div>
                ))}
            </dl>
        </section>
    );
}

export default SpeechRatioBar;
